import { lookupCountry } from './countryCoordinates';

/** Marker fill colors per entity type (kept in sync with the map legend). */
export const MARKER_COLORS = {
    country: '#007bff',
    location: '#28a745',
};

/**
 * Extract a display name from an entity that may be a string or an object
 * ({name}, {value} or {text}, as produced by the NLP service and GraphQL).
 * @param {string|object} entity
 * @returns {string}
 */
export const getEntityName = (entity) => {
    if (typeof entity === 'string') return entity.trim();
    if (entity && typeof entity === 'object') {
        return String(entity.name || entity.value || entity.text || '').trim();
    }
    return '';
};

/** Mention count of an entity; defaults to 1 when absent or invalid. */
const getEntityCount = (entity) => {
    const count = entity && typeof entity === 'object' ? Number(entity.count) : NaN;
    return count > 0 ? count : 1;
};

/** ISO code of an entity if one was provided (NLP uses isoCode, GraphQL uses code). */
const getEntityIsoCode = (entity) =>
    entity && typeof entity === 'object' ? entity.isoCode || entity.iso_code || entity.code : undefined;

/**
 * Convert entities of one type into map points, merging duplicates
 * (e.g. "USA" and "United States") into a single point whose count is summed.
 * Entities that cannot be geolocated are skipped.
 */
const toMapPoints = (entities, type) => {
    const points = new Map();
    (Array.isArray(entities) ? entities : []).forEach((entity) => {
        const name = getEntityName(entity);
        const country = name ? lookupCountry(name, getEntityIsoCode(entity)) : null;
        if (!country) return;

        const existing = points.get(country.code);
        if (existing) {
            existing.count += getEntityCount(entity);
            return;
        }
        points.set(country.code, {
            id: `${type}-${country.code}`,
            name: type === 'country' ? country.name : name,
            type,
            coordinates: country.coordinates,
            count: getEntityCount(entity),
        });
    });
    return [...points.values()];
};

/**
 * Build the list of map points for the Geographic Map.
 * @param {Array<string|object>} countries
 * @param {Array<string|object>} locations
 * @returns {Array<{id: string, name: string, type: string, coordinates: number[], count: number}>}
 */
export const buildMapData = (countries = [], locations = []) => [
    ...toMapPoints(countries, 'country'),
    ...toMapPoints(locations, 'location'),
];

/**
 * Circle radius (px) for a point, scaled by mention frequency.
 * @param {number} count
 * @returns {number}
 */
export const getMarkerSize = (count) => {
    if (count <= 1) return 8;
    if (count <= 5) return 12;
    if (count <= 10) return 16;
    return 20;
};

/** Marker color for an entity type, defaulting to the location color. */
export const getMarkerColor = (type) => MARKER_COLORS[type] || MARKER_COLORS.location;

/**
 * Count map points per type for the footer summary.
 * @param {Array<{type: string}>} mapData
 * @returns {{countries: number, locations: number}}
 */
export const summarizeMapData = (mapData) => ({
    countries: mapData.filter((item) => item.type === 'country').length,
    locations: mapData.filter((item) => item.type === 'location').length,
});
